import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { AnimatePresence, Variants, motion } from 'motion/react'

import { useNavigate } from '../../navigation/custom-react-router-dom'
import { useMenu } from '../context/MenuContext'
import { MenuLink } from './MenuLink'
import { MenuSection as MenuSectionType } from './menuSections/useMenuSections'

import styles from './MenuSection.module.scss'

interface MenuSectionProps {
  section: MenuSectionType
  isTopLevel?: boolean
}

export const MenuSection: React.FC<MenuSectionProps> = ({ section, isTopLevel }) => {
  const isSection = !!section.children?.length
  const isLanguageItem = section.id === 'language'
  const isLogoutItem = section.id === 'logout'
  const navigate = useNavigate()
  const {
    isMenuOpen,
    isScreenMenuOpen,
    toggleSectionExpand,
    setScreenMenuOpen,
    expandedSections,
    closeMenu,
  } = useMenu()
  const location = useLocation()
  const [isScreenOpen, setIsScreenOpen] = useState(true)

  const subMenuVariants: Variants = {
    expanded: {
      opacity: 1,
      height: 'auto',
      marginTop: '0.5rem',
      pointerEvents: 'all',
      transition: { duration: 0.3, ease: 'linear' },
    },
    collapsed: {
      opacity: 0,
      height: 0,
      marginTop: 0,
      pointerEvents: 'none',
      transition: { duration: 0.3, ease: 'linear' },
    },
  }

  const isSectionActive = (section: MenuSectionType): boolean => {
    if (section.path && location.pathname.includes(section.path)) {
      // handling for platform action items - only show active if on exact platforms path
      if (section.id === 'create_ta_cfd_action' || section.id === 'create_ta_etd_action') {
        const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}\//, '/')
        return pathWithoutLang === section.path
      }
      return true
    }
    return section.children?.some(isSectionActive) ?? false
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()

    const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}\//, '/')

    if (section.path) {
      const isCurrentPage = pathWithoutLang === section.path

      if (isCurrentPage) {
        closeMenu()
      } else {
        navigate(section.path)
      }
    } else if (section.children?.length) {
      toggleSectionExpand(section.id)
    } else if (section.screenChildren?.length) {
      setIsScreenOpen(true)
      setScreenMenuOpen(true)
    } else if (section.onClick) {
      section.onClick()
    }
  }

  return (
    <>
      <li
        className={classNames(styles.menuItem, {
          [styles.section]: isSection || isLanguageItem || isLogoutItem,
          [styles.sectionItem]: !isSection && !isLanguageItem && !isLogoutItem,
        })}
      >
        <MenuLink
          section={section}
          isActive={isSectionActive(section)}
          onClick={handleClick}
          isTopLevel={isTopLevel}
          onExpandClick={() => toggleSectionExpand(section.id)}
        />

        {!!section.children?.length && (
          <AnimatePresence>
            {isMenuOpen && (
              <motion.ul
                className={styles.subMenuList}
                variants={subMenuVariants}
                initial='collapsed'
                animate={expandedSections.includes(section.id) ? 'expanded' : 'collapsed'}
                exit='collapsed'
              >
                {section.children.map((subSection) => (
                  <MenuSection section={subSection} key={subSection.id} />
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        )}
      </li>

      {!!section.screenChildren?.length && (
        <AnimatePresence mode='wait' onExitComplete={() => setScreenMenuOpen(false)}>
          {isScreenOpen && isScreenMenuOpen && (
            <motion.div
              className={styles.screenContainer}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '300%', transition: { duration: 0.6, type: 'tween' } }}
              transition={{ type: 'tween', duration: 0.2 }}
            >
              <MenuLink
                section={section}
                isActive={isSectionActive(section)}
                onClick={() => {
                  setIsScreenOpen(false)
                }}
                isScreenMenu
                isTopLevel={isTopLevel}
              />
              <ul className={styles.screenMenuList}>
                {section.screenChildren.map((subSection) => (
                  <MenuSection section={subSection} key={subSection.id} />
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  )
}
